import { h } from "./../../utils/createElement";
import { __extends } from "tslib";
import * as React from 'react';
import dayjs from 'dayjs';
import Dropdown from '../Dropdown';
import ComboBox from './ComboBox';
import { createTimeColumns, MAX_HOUR, MAX_MINUTE, MAX_SECOND } from './timeCells';
import { emptyFn, castInto, roundByStep, createStringParamFunc, trueFn } from '../../utils/helper';
import { consumeTheme } from '../../utils/theme';
import Button from '../Button';
import { i18n } from '../../common/locale';
var DROPDOWN_TRANSITION_DURATION = 200;
var TimePicker = (function (_super) {
    __extends(TimePicker, _super);
    function TimePicker() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.state = {
            currentValue: _this.props.defaultValue || _this.getFirstValidDate(),
            hourInputFocus: false,
            minuteInputFocus: false,
            secondInputFocus: false
        };
        _this.comboBoxRef = React.createRef();
        _this.handlePickChange = function (values) {
            if (_this.props.disabled) {
                return;
            }
            var nextDate = _this.createDayjsFromValues(values);
            _this.setState({ currentValue: nextDate });
            _this.props.onChange(nextDate);
        };
        _this.handleHourInputChange = function (e) {
            _this.handleInputChange('hour', e.target.value, false);
        };
        _this.handleMinuteInputChange = function (e) {
            _this.handleInputChange('minute', e.target.value, false);
        };
        _this.handleSecondInputChange = function (e) {
            _this.handleInputChange('second', e.target.value, false);
        };
        _this.handleHourInputFocus = function () {
            _this.setState({ hourInputFocus: true });
        };
        _this.handleHourInputBlur = function (e) {
            _this.setState({ hourInputFocus: false });
            _this.handleInputChange('hour', e.target.value, true);
        };
        _this.handleMinuteInputFocus = function () {
            _this.setState({ minuteInputFocus: true });
        };
        _this.handleMinuteInputBlur = function (e) {
            _this.setState({ minuteInputFocus: false });
            _this.handleInputChange('minute', e.target.value, true);
        };
        _this.handleSecondInputFocus = function () {
            _this.setState({ secondInputFocus: true });
        };
        _this.handleSecondInputBlur = function (e) {
            _this.setState({ secondInputFocus: false });
            _this.handleInputChange('second', e.target.value, true);
        };
        _this.handleComboBoxVisibleChange = function (visible) {
            if (!visible) {
                setTimeout(function () { return _this.scrollComboBoxToSelected(); }, DROPDOWN_TRANSITION_DURATION);
            }
            _this.props.onVisibleChange(visible);
        };
        _this.handleInputSectionMouseDown = function (e) {
            if (_this.props.disableUserInput) {
                e.preventDefault();
            }
        };
        _this.handleInputSectionClick = function (e) {
            if (!_this.props.disableUserInput) {
                e.stopPropagation();
            }
        };
        _this.setToNow = function () {
            var value = dayjs();
            _this.setState({ currentValue: value });
            _this.props.onChange(value);
            setTimeout(_this.handleOk, 0);
        };
        _this.handleOk = function () {
            _this.props.onOk(_this.getCurrentValue());
        };
        _this.themedRender = function (classNames) {
            var _a = _this.props, prefixCls = _a.prefixCls, className = _a.className, style = _a.style, hourStep = _a.hourStep, minuteStep = _a.minuteStep, secondStep = _a.secondStep, showSecond = _a.showSecond, disabled = _a.disabled, shouldEnableHour = _a.shouldEnableHour, shouldEnableMinute = _a.shouldEnableMinute, shouldEnableSecond = _a.shouldEnableSecond, input = _a.input, visible = _a.visible, dropdownAutoFocus = _a.dropdownAutoFocus, embedded = _a.embedded, showFooter = _a.showFooter;
            var value = _this.getCurrentValue();
            var selectedValues = _this.getValuesFromDayjs(value);
            var columns = createTimeColumns(hourStep, minuteStep, secondStep, showSecond);
            var cls = classNames(prefixCls, className);
            var comboDisableFuncs = [shouldEnableHour, shouldEnableMinute, shouldEnableSecond]
                .map(function (numEnableFunc) { return createStringParamFunc(numEnableFunc); });
            var dropContent = (h("div", { className: classNames(prefixCls + "-popup") },
                h(Dropdown.Keep, { className: classNames(prefixCls + "-combobox-container") },
                    h(ComboBox, { columns: columns, onChange: _this.handlePickChange, selectedValues: selectedValues, ref: _this.comboBoxRef, shouldEnableValues: comboDisableFuncs, columnLabels: [i18n('hour'), i18n('minute'), i18n('second')] })),
                showFooter && h("div", { className: classNames(prefixCls + "-actions") },
                    h("div", { onClick: _this.setToNow, className: classNames(prefixCls + "-actions-now") },
                        h("div", { className: classNames(prefixCls + "-actions-now-icon") }),
                        i18n('now')),
                    h(Button, { type: "primary", size: "small", className: classNames(prefixCls + "-actions-ok"), onClick: _this.handleOk }, i18n('ok')))));
            return (h("div", { className: cls, style: style }, embedded
                ? dropContent
                : h(Dropdown, { dropContent: dropContent, disabled: disabled, onVisibleChange: _this.handleComboBoxVisibleChange, destroyOnClose: false, visible: visible, placement: "auto", alignment: "auto", autoFocus: dropdownAutoFocus }, input || _this.renderDefaultInput(classNames))));
        };
        return _this;
    }
    TimePicker.prototype.getFirstValidDate = function () {
        var _a = this.props, hourStep = _a.hourStep, minuteStep = _a.minuteStep, secondStep = _a.secondStep, shouldEnableHour = _a.shouldEnableHour, shouldEnableMinute = _a.shouldEnableMinute, shouldEnableSecond = _a.shouldEnableSecond;
        var hour = this.correctValue(0, hourStep, MAX_HOUR, shouldEnableHour);
        var minute = this.correctValue(0, minuteStep, MAX_MINUTE, shouldEnableMinute.bind(null, hour));
        var second = this.correctValue(0, secondStep, MAX_SECOND, shouldEnableSecond.bind(null, hour, minute));
        return this.createDayjsFromValues([hour, minute, second], dayjs());
    };
    TimePicker.prototype.getCurrentValue = function () {
        return this.props.value || this.state.currentValue;
    };
    TimePicker.prototype.handleInputChange = function (type, newValue, final) {
        var _this = this;
        var _a = this.props, disabled = _a.disabled, disableUserInput = _a.disableUserInput, hourStep = _a.hourStep, minuteStep = _a.minuteStep, secondStep = _a.secondStep, shouldEnableHour = _a.shouldEnableHour, shouldEnableMinute = _a.shouldEnableMinute, shouldEnableSecond = _a.shouldEnableSecond;
        if (disabled || disableUserInput) {
            return;
        }
        var prevDayjs = this.getCurrentValue();
        var _b = this.getValuesFromDayjs(prevDayjs).map(function (str) { return Number(str); }), prevHour = _b[0], prevMinute = _b[1];
        var value = Number(newValue);
        var nextDayjs = null;
        if (isNaN(value)) {
            return;
        }
        if (type === 'hour') {
            var nextHour = castInto(value, [0, MAX_HOUR]);
            nextDayjs = prevDayjs.set('hour', final ?
                this.correctValue(nextHour, hourStep, MAX_HOUR, shouldEnableHour)
                : nextHour);
        }
        else if (type === 'minute') {
            var nextMinute = castInto(value, [0, MAX_MINUTE]);
            nextDayjs = prevDayjs.set('minute', final ?
                this.correctValue(nextMinute, minuteStep, MAX_MINUTE, shouldEnableMinute.bind(null, prevHour))
                : nextMinute);
        }
        else if (type === 'second') {
            var nextSecond = castInto(value, [0, MAX_SECOND]);
            nextDayjs = prevDayjs.set('second', final ?
                this.correctValue(nextSecond, secondStep, MAX_SECOND, shouldEnableSecond.bind(null, prevHour, prevMinute))
                : nextSecond);
        }
        this.setState({ currentValue: nextDayjs });
        if (final) {
            this.props.onChange(nextDayjs);
            setTimeout(function () { return _this.scrollComboBoxToSelected(); }, 0);
        }
    };
    TimePicker.prototype.correctValue = function (value, step, max, isEnabled) {
        var roundedValue = roundByStep(value, step);
        if (isEnabled(roundedValue) && 0 <= roundedValue && roundedValue <= max) {
            return roundedValue;
        }
        var next = roundedValue + step;
        var prev = roundedValue - step;
        while (next <= max || prev >= 0) {
            if (next <= max && isEnabled(next)) {
                return next;
            }
            if (prev >= 0 && isEnabled(prev)) {
                return prev;
            }
            next += step;
            prev -= step;
        }
        throw new Error('TimePicker 参数无效，您可能禁用了所有值！');
    };
    TimePicker.prototype.scrollComboBoxToSelected = function () {
        var comboBox = this.comboBoxRef.current;
        if (comboBox) {
            comboBox.scrollToSelected();
        }
    };
    TimePicker.prototype.createDayjsFromValues = function (values, currentValue) {
        if (currentValue === void 0) { currentValue = this.getCurrentValue(); }
        var resolvedValues = {
            hour: Number(values[0]),
            minute: Number(values[1]),
            second: this.props.showSecond ? Number(values[2]) : 0,
            millisecond: 0
        };
        return Object.getOwnPropertyNames(resolvedValues).reduce(function (time, key) { return time.set(key, resolvedValues[key]); }, currentValue);
    };
    TimePicker.prototype.getValuesFromDayjs = function (date) {
        return [
            String(date.hour()),
            String(date.minute()),
            String(date.second()),
        ];
    };
    TimePicker.prototype.componentDidMount = function () {
        this.scrollComboBoxToSelected();
    };
    TimePicker.prototype.renderDefaultInput = function (classNames) {
        var _a, _b;
        var _c = this.props, prefixCls = _c.prefixCls, showSecond = _c.showSecond, disabled = _c.disabled, disableUserInput = _c.disableUserInput, underline = _c.underline;
        var _d = this.state, hourInputFocus = _d.hourInputFocus, minuteInputFocus = _d.minuteInputFocus, secondInputFocus = _d.secondInputFocus;
        var focus = hourInputFocus || minuteInputFocus || secondInputFocus;
        var value = this.getCurrentValue();
        var selectedValues = this.getValuesFromDayjs(value);
        var hour = selectedValues[0], minute = selectedValues[1], second = selectedValues[2];
        var iconCls = classNames(prefixCls + "-icon", (_a = {},
            _a[prefixCls + "-icon-disabled"] = disabled,
            _a));
        var containerCls = classNames(prefixCls + "-input-container", (_b = {},
            _b[prefixCls + "-input-container-underline"] = underline,
            _b[prefixCls + "-input-container" + (underline ? '-underline' : '') + "-focus"] = focus,
            _b[prefixCls + "-input-container-disabled"] = disabled,
            _b));
        return (h("div", { className: containerCls },
            h("div", { className: iconCls }),
            h("div", { className: classNames(prefixCls + "-text-container"), onMouseDown: this.handleInputSectionMouseDown, onClick: this.handleInputSectionClick },
                h("input", { type: "text", value: hour, onChange: this.handleHourInputChange, className: classNames(prefixCls + "-input"), onFocus: this.handleHourInputFocus, onBlur: this.handleHourInputBlur, disabled: disabled || disableUserInput, "aria-label": i18n('inputHour') }),
                h("span", null, i18n('hour')),
                h("input", { type: "text", value: minute, onChange: this.handleMinuteInputChange, className: classNames(prefixCls + "-input"), onFocus: this.handleMinuteInputFocus, onBlur: this.handleMinuteInputBlur, disabled: disabled || disableUserInput, "aria-label": i18n('inputMinute') }),
                h("span", null, i18n('minute')),
                showSecond ?
                    h(React.Fragment, null,
                        h("input", { type: "text", value: second, onChange: this.handleSecondInputChange, className: classNames(prefixCls + "-input"), onFocus: this.handleSecondInputFocus, onBlur: this.handleSecondInputBlur, disabled: disabled || disableUserInput, "aria-label": i18n('inputSecond') }),
                        h("span", null, i18n('second')))
                    : null)));
    };
    TimePicker.prototype.render = function () {
        return consumeTheme(this.themedRender);
    };
    return TimePicker;
}(React.Component));
export default TimePicker;
TimePicker.defaultProps = {
    prefixCls: 'dui-timepicker',
    disableUserInput: false,
    onChange: emptyFn,
    hourStep: 1,
    minuteStep: 1,
    secondStep: 1,
    showSecond: true,
    disabled: false,
    shouldEnableHour: trueFn,
    shouldEnableMinute: trueFn,
    shouldEnableSecond: trueFn,
    underline: false,
    onVisibleChange: emptyFn,
    onOk: emptyFn,
    dropdownAutoFocus: true,
    showFooter: true,
};
